import { useRef, useState } from "react";
import { useReactFlow, Handle, Position, NodeResizer } from "@xyflow/react";
import { Button, Input, Modal, Select } from "antd";
import { MailOutlined, CloseOutlined, SettingOutlined, CopyOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import NodeNameInput from "./NodeNameInput";

const { Option } = Select;

const ATTRIBUTES = [
  { label: "First Name",   value: "{{firstname}}" },
  { label: "Last Name",    value: "{{lastname}}" },
  { label: "Email",        value: "{{email}}" },
  { label: "Phone",        value: "{{phone}}" },
  { label: "Designation",  value: "{{designation}}" },
  { label: "Sender Name",  value: "{{senderName}}" },
];

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ align: [] }],
    ["blockquote", "link"],
    ["clean"],
  ],
};

const QUILL_FORMATS = [
  "header", "bold", "italic", "underline", "strike",
  "list", "bullet", "align", "blockquote", "link",
];

const SendEmailNode = ({ data, selected, id }) => {
  const { deleteElements } = useReactFlow();
  const userProfile = useSelector((s) => s.profile);
  const quillRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState(data.subject || "");
  const [body, setBody] = useState(data.body || "");
  const [emailProvider, setEmailProvider] = useState(data.emailProvider || "");
  const [fromEmail, setFromEmail] = useState(data.fromEmail || "");
  const [toEmail, setToEmail] = useState(data.toEmail || "");

  const dataRef = useRef(data);
  dataRef.current = data;

  const connectedAccounts = (userProfile?.accounts?.email || [])
    .filter((a) => a.isConnected)
    .map((a) => ({
      label: `${a.type.charAt(0).toUpperCase() + a.type.slice(1)} (${a.email})`,
      value: a.type,
      email: a.email,
    }));

  const emailTemplates = userProfile?.templates?.emailTemplates?.emailTemplatesData || [];

  const openModal = () => {
    setSubject(dataRef.current.subject || "");
    setBody(dataRef.current.body || "");
    setEmailProvider(dataRef.current.emailProvider || connectedAccounts[0]?.value || "");
    setFromEmail(dataRef.current.fromEmail || connectedAccounts[0]?.email || "");
    setToEmail(dataRef.current.toEmail || "");
    setOpen(true);
  };

  const handleSave = () => {
    dataRef.current.onChange?.({
      ...dataRef.current,
      subject,
      body,
      emailProvider,
      fromEmail,
      toEmail,
    });
    setOpen(false);
  };

  const insertAttribute = (val) => {
    if (!val) return;
    const editor = quillRef.current?.getEditor();
    if (editor) {
      const range = editor.getSelection(true);
      const idx = range ? range.index : editor.getLength();
      editor.insertText(idx, val);
      editor.setSelection(idx + val.length);
    }
  };

  const handleTemplateSelect = (templateId) => {
    if (!templateId) return;
    const tpl = emailTemplates.find((t) => t.emailTemplate_id === templateId);
    if (tpl) {
      setSubject(tpl.emailTemplateSubject || "");
      setBody(tpl.emailTemplateBody || "");
    }
  };

  const handleProviderChange = (val) => {
    setEmailProvider(val);
    const acc = connectedAccounts.find((a) => a.value === val);
    setFromEmail(acc?.email || "");
  };

  const previewSubject = data.subject
    ? data.subject.length > 35 ? data.subject.slice(0, 35) + "…" : data.subject
    : null;

  return (
    <>
      <div
        style={{
          background: "#fff",
          border: `2px solid ${selected ? "#3b82f6" : "#e5e7eb"}`,
          borderRadius: 10,
          padding: 14,
          minWidth: 260,
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          width: "100%",
          height: "100%",
          boxSizing: "border-box",
        }}
      >
        <NodeResizer minWidth={240} minHeight={80} isVisible={selected} color="#3b82f6" />
        <Handle type="target" position={Position.Left} style={{ background: "#3b82f6" }} />

        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <MailOutlined style={{ color: "#3b82f6", fontSize: 16 }} />
          <strong style={{ fontSize: 13 }}>Send Email</strong>
          <Button
            type="text" size="small" icon={<CopyOutlined />}
            onClick={() => data.onDuplicate?.()}
            style={{ marginLeft: "auto", color: "#9ca3af" }}
          />
          <Button
            type="text" size="small" icon={<CloseOutlined />}
            onClick={() => deleteElements({ nodes: [{ id }] })}
            style={{ color: "#9ca3af" }}
          />
        </div>

        <div className="nodrag" onPointerDown={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
          <NodeNameInput data={data} />
          {data.emailProvider && (
            <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 4 }}>
              From: <strong>{data.fromEmail || data.emailProvider}</strong>
            </div>
          )}
          {previewSubject ? (
            <div style={{ fontSize: 11, color: "#374151", marginBottom: 8 }}>
              Subject: <strong>{previewSubject}</strong>
            </div>
          ) : (
            <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 8 }}>Not configured</div>
          )}
          <Button
            size="small"
            icon={<SettingOutlined />}
            onClick={openModal}
            style={{ width: "100%", fontSize: 12 }}
          >
            Configure Email
          </Button>
        </div>

        <Handle type="source" position={Position.Right} style={{ background: "#3b82f6" }} />
      </div>

      <Modal
        title="Configure Send Email"
        open={open}
        onCancel={() => setOpen(false)}
        width={700}
        footer={null}
        styles={{ body: { maxHeight: "75vh", overflowY: "auto" } }}
        destroyOnClose={false}
      >
        {/* Template */}
        <div style={{ marginBottom: 16 }}>
          <Select
            placeholder="Select a template (optional)"
            style={{ width: "100%" }}
            allowClear
            onChange={handleTemplateSelect}
            options={emailTemplates.map((t) => ({ label: t.emailTemplateTitle, value: t.emailTemplate_id }))}
          />
        </div>

        {/* From */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", fontWeight: 500, marginBottom: 6 }}>From</label>
          <Select
            style={{ width: "100%" }}
            value={emailProvider || undefined}
            onChange={handleProviderChange}
            placeholder="Select email account"
            options={connectedAccounts}
            notFoundContent={<span style={{ color: "#9ca3af" }}>No email account connected — go to Sync &amp; Integration</span>}
          />
        </div>

        {/* To */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", fontWeight: 500, marginBottom: 6 }}>
            To <span style={{ fontWeight: 400, color: "#6b7280", fontSize: 12 }}>(leave blank to send to contact's email)</span>
          </label>
          <Input
            value={toEmail}
            onChange={(e) => setToEmail(e.target.value)}
            placeholder="e.g. admin@company.com or leave blank for {{email}}"
          />
        </div>

        {/* Subject */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", fontWeight: 500, marginBottom: 6 }}>Subject</label>
          <Input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="e.g. Hello {{firstname}}, welcome!"
          />
        </div>

        {/* Attributes */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", fontWeight: 500, marginBottom: 6 }}>Attributes</label>
          <select
            className="form-select"
            onChange={(e) => {
              insertAttribute(e.target.value);
              e.target.selectedIndex = 0;
            }}
          >
            <option value="">Insert Field</option>
            {ATTRIBUTES.map((a) => (
              <option key={a.value} value={a.value}>{a.label}</option>
            ))}
          </select>
          <div style={{ marginTop: 4, fontSize: 11, color: "#9ca3af" }}>
            Select an attribute to insert it at the cursor position in the body
          </div>
        </div>

        {/* Body */}
        <div style={{ marginBottom: 24 }}>
          <label style={{ display: "block", fontWeight: 500, marginBottom: 6 }}>Body</label>
          <ReactQuill
            ref={quillRef}
            theme="snow"
            value={body}
            onChange={setBody}
            modules={QUILL_MODULES}
            formats={QUILL_FORMATS}
            placeholder="Enter email body here..."
            style={{ height: 260, display: "flex", flexDirection: "column" }}
          />
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 48 }}>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button type="primary" onClick={handleSave} disabled={!emailProvider || !subject}>
            Save
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default SendEmailNode;
